import { useCallback, useEffect, useState } from 'react';
import {
  CircleDot, MapPin, Wifi, WifiOff, LogOut, Activity, TriangleAlert,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import NotaExplicativa from '../ui/NotaExplicativa';
import Motivo from '../experiencia/Motivo';
import ChipEstado from '../ui/ChipEstado';
import { ESTADO_ESTACION } from '../ui/estados';
import { fechaYHora } from '../ui/sesiones';
import { Ppm } from '../terminal/Planta';
import AmbarDeLaEstacion from '../terminal/AmbarDeLaEstacion';

// EL GEMELO DE LA ESTACIÓN · Anexo B, §B7
//
// QUÉ CONTESTA, Y ES UNA SOLA PREGUNTA:
//
//     «¿Qué está pasando AHORA en Línea 2?»
//
// Es la pantalla que se enseña en una demo, así que lleva la misma regla
// que la terminal: **ni un peso**. No es que se filtre — es que la ruta
// no lo devuelve, y hay una prueba que mira el cuerpo crudo.
//
// SE REFRESCA SOLA, cada diez segundos. Una pantalla que dice «ahora» y
// hay que recargar a mano no dice «ahora»: dice «cuando alguien miró».
// Y se dice en la propia pantalla cuándo fue la última vez, porque un
// dato que se refresca solo y no dice cuándo es exactamente el ícono
// verde que miente.

const MS_REFRESCO = 10_000;

function minutosDesde(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
}

export default function Estacion({ codigo: codigoInicial = '' }) {
  const [codigo, setCodigo] = useState(codigoInicial);
  const [buscado, setBuscado] = useState(codigoInicial);
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [visto, setVisto] = useState(null);

  const cargar = useCallback(() => {
    if (!buscado) return;
    api.get(`/estaciones/${encodeURIComponent(buscado)}`)
      .then((x) => { setD(x); setVisto(Date.now()); setError(null); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.'));
  }, [buscado]);

  useEffect(cargar, [cargar]);
  useEffect(() => {
    const t = setInterval(cargar, MS_REFRESCO);
    return () => clearInterval(t);
  }, [cargar]);

  async function cerrarSesion() {
    try {
      await api.del(`/dispositivos/sesiones/${d.id_sesion}`);
      cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cerrar.');
    }
  }

  return (
    <div className="space-y-5 max-w-3xl">
      <header>
        <h1 className="text-xl font-bold text-gray-900">Cómo va una estación</h1>
        <p className="text-sm text-gray-600">
          Lo que está pasando ahora mismo en una repisa o en una línea:
          quién está, cuánto lleva hecho y qué se ha apuntado.
        </p>
      </header>

      <form
        onSubmit={(e) => { e.preventDefault(); setBuscado(codigo.trim().toUpperCase()); }}
        className="flex gap-2 items-end"
      >
        <div className="flex-1">
          <label htmlFor="est-codigo" className="block text-sm font-medium text-gray-700">
            Código de la estación
          </label>
          <input
            id="est-codigo"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            placeholder="LIN-2"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            El mismo código que tiene en Administración → Almacén.
          </p>
        </div>
        <button
          type="submit"
          disabled={!codigo.trim()}
          aria-describedby={codigo.trim() ? undefined : 'motivo-ver-estacion'}
          className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          Ver
        </button>
        <Motivo id="motivo-ver-estacion" cuando={!codigo.trim()}>Escribe el código de la estación para verla.</Motivo>
      </form>

      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      {!buscado && (
        <div className="border border-gray-200 bg-white rounded-xl p-5 text-center space-y-2">
          <Activity className="h-8 w-8 mx-auto text-gray-300" />
          <div className="font-semibold text-gray-900">Escribe un código arriba</div>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Esta pantalla enseña una estación en vivo. Sirve para saber sin
            caminar hasta allá si alguien está trabajando en ella, cuánto
            lleva y si algo se está yendo de las manos.
          </p>
        </div>
      )}

      {d && <Tarjeta d={d} visto={visto} onCerrar={cerrarSesion} />}
      {d && <Rastro ultimos={d.ultimos} />}
      {/* TC-20: el ámbar de las baldas de ESTA repisa, y TC-15: el PPM
          de 30 días. Los dos leen rutas que ya existían. */}
      {d && <AmbarDeLaEstacion ubicacion={d.ubicacion} idUbicacion={d.id_ubicacion} />}
      {d && d.id_ubicacion && <Ppm idUbicacion={d.id_ubicacion} />}
    </div>
  );
}

export function Tarjeta({ d, visto, onCerrar }) {
  const quieta = minutosDesde(d.ultimo_latido);
  return (
    <div className="border border-gray-200 bg-white rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-3">
        <MapPin className="h-5 w-5 mt-0.5 shrink-0 text-gray-400" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-900">
            {d.ubicacion}{d.ubicacion_nombre ? ` · ${d.ubicacion_nombre}` : ''}
          </div>
          {d.aparato && (
            <div className="text-sm text-gray-600 flex items-center gap-1">
              <CircleDot size={13} className="shrink-0" />
              {d.aparato}
              {d.version_firmware ? ` · versión ${d.version_firmware}` : ''}
            </div>
          )}
          {/* NUNCA UN ÍCONO VERDE QUE MIENTE: se dice cuándo habló, no
              «conectada» a secas. */}
          {d.id_dispositivo && (
            <div className="text-sm text-gray-600 flex items-center gap-1">
              {d.en_linea
                ? <><Wifi size={13} className="text-green-700 shrink-0" /> Dio señales hace un momento</>
                : <><WifiOff size={13} className="text-gray-400 shrink-0" />{' '}
                    {d.ultimo_latido
                      ? `Sin señales desde hace ${quieta} min`
                      : 'Todavía no se ha conectado ni una vez'}</>}
            </div>
          )}
          {d.operador && (
            <div className="text-sm text-gray-700">
              <strong>{d.operador}</strong> desde el {fechaYHora(d.desde)}
              {d.turno ? ` · turno ${d.turno}` : ''}
            </div>
          )}
        </div>
        <ChipEstado mapa={ESTADO_ESTACION} clave={d.estado} />
      </div>

      {/* SE DICE, NO SE ESCONDE (§B1): quien cubre a un compañero
          trabaja igual, y el encargado se entera aquí. */}
      {d.fuera_de_asignacion && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2">
          <TriangleAlert size={15} className="mt-0.5 shrink-0" />
          <span>
            Quien está aquí no era quien se esperaba en este turno. No es
            un error: puede estar cubriendo a alguien. Queda apuntado.
          </span>
        </div>
      )}

      {d.mensaje && <p className="text-sm text-gray-600">{d.mensaje}</p>}

      <Numeros d={d} />

      <div className="flex flex-wrap items-center gap-3 text-sm">
        {d.pendientes_sin_subir > 0 && (
          <span className="text-amber-800 font-semibold">
            {d.pendientes_sin_subir} sin subir
          </span>
        )}
        {d.id_sesion && onCerrar && (
          <button
            type="button"
            onClick={onCerrar}
            className="inline-flex items-center gap-1.5 border border-gray-300 bg-white rounded-lg px-3 py-1.5 font-semibold text-gray-700"
          >
            <LogOut size={15} />
            Cerrar la sesión de {d.operador}
          </button>
        )}
        {visto && (
          <span className="text-xs text-gray-600 ml-auto">
            Se actualiza sola · última vez {fechaYHora(new Date(visto).toISOString())}
          </span>
        )}
      </div>
    </div>
  );
}

// LOS NÚMEROS DEL DÍA.
//
// NULO NO ES CERO, y aquí es donde más importa: si nadie contó la
// producción, el PPM no se puede calcular. Enseñar un cero diría «aquí
// no pasa nada» cuando lo que pasa es que no se sabe — y es el número
// que el cliente audita.
export function Numeros({ d }) {
  const conObjetivo = d.objetivo_por_hora != null;
  return (
    <div className="grid grid-cols-3 gap-3 text-center">
      <div className="border border-gray-200 rounded-lg p-2">
        <div className="text-2xl font-bold tabular-nums text-gray-900">
          {d.buenas_hoy}
        </div>
        <div className="text-xs text-gray-500">
          buenas hoy
          {conObjetivo ? ` · meta ${d.objetivo_por_hora}/h` : ''}
        </div>
      </div>
      <div className="border border-gray-200 rounded-lg p-2">
        <div className="text-2xl font-bold tabular-nums text-gray-900">
          {d.rechazadas_hoy}
        </div>
        <div className="text-xs text-gray-500">malas hoy</div>
      </div>
      <div className="border border-gray-200 rounded-lg p-2">
        <div className="text-2xl font-bold tabular-nums text-gray-900">
          {d.sin_produccion_contada || d.ppm_hoy == null ? '—' : d.ppm_hoy}
        </div>
        <div className="text-xs text-gray-500">
          {d.sin_produccion_contada
            ? 'PPM: nadie contó la producción'
            : 'PPM de hoy'}
        </div>
      </div>
    </div>
  );
}

export function Rastro({ ultimos }) {
  if (!ultimos || ultimos.length === 0) {
    return (
      <NotaExplicativa titulo="Todavía no ha pasado nada por aquí">
        Cuando alguien mueva, cuente o rechace algo en esta estación,
        aparecerá en esta lista con su hora y su nombre.
      </NotaExplicativa>
    );
  }
  return (
    <div className="space-y-2">
      <h2 className="text-base font-bold text-gray-900">Lo último que pasó</h2>
      <div className="border border-gray-200 bg-white rounded-xl divide-y divide-gray-100">
        {ultimos.map((m, i) => (
          <div key={i} className="p-3 flex items-start gap-3 text-sm">
            <span className={`font-semibold shrink-0 ${
              m.que === 'RECHAZO' ? 'text-red-700' : 'text-gray-700'}`}>
              {m.que === 'RECHAZO' ? 'Pieza mala' : m.que}
            </span>
            <span className="min-w-0 flex-1 truncate text-gray-700">
              {m.producto || m.sku || '—'}
            </span>
            <span className="tabular-nums shrink-0 text-gray-900">{m.cantidad}</span>
            <span className="shrink-0 text-gray-500 hidden sm:inline">
              {m.quien || '—'}
            </span>
            <span className="shrink-0 text-xs text-gray-600">
              {fechaYHora(m.cuando)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
